import React, {useState, useRef} from 'react';
import {Badge, Button, Spinner} from 'react-bootstrap';
import {isValidNumericInput, isValidIntegerInput, isNumericFieldType, isIntegerFieldType} from '../utils/numberInput';
import {
    formatTimestampForDisplay,
    formatDateForDisplay,
    formatMonthYearForDisplay,
    toTimeInput,
    datetimeLocalToISO,
    secondsToInterval,
} from '../utils/timestampUtils';
import {DateTimePicker} from './DateTimePicker';
import {DurationPicker} from './DurationPicker';
import {downloadFile, FileData, isDisplayableImage, getFileName} from '../utils/fileUtils';
import {getValueByPath, ExpandedColumn} from '../utils/columnExpander';
import {mergeUi, resolveAppearance} from '../utils/uiUtils';
import {resolveAssignShape} from '../utils/oneToManyAssign';
import {IconDownload, IconExternalLink, IconLink, IconX} from '@tabler/icons-react';
import {AuthenticatedImage} from './AuthenticatedImage';
import {ImageModal} from './ImageModal';
import {SelectModal} from './SelectModal';

interface EditableTableCellProps {
    row: any;
    column: ExpandedColumn;
    isEditing: boolean;
    editValue: any;
    onEditValueChange: (value: any) => void;
    onCellClick: (row: any, column: ExpandedColumn, e: React.MouseEvent) => void;
    onCellBlur: () => void;
    onDirectUpdate?: (row: any, column: ExpandedColumn, value: any) => void;
    onOneToManyDelete?: (column: ExpandedColumn, childId: number | string) => void;
    onOneToManyAdd?: (column: ExpandedColumn, row: any) => void;
    onOneToManyAssign?: (column: ExpandedColumn, row: any) => void;
    onBeforeOpenSelectModal?: (row: any, column: ExpandedColumn) => void;
}

export const EditableTableCell: React.FC<EditableTableCellProps> = ({
                                                                        row,
                                                                        column,
                                                                        isEditing,
                                                                        editValue,
                                                                        onEditValueChange,
                                                                        onCellClick,
                                                                        onCellBlur,
                                                                        onDirectUpdate,
                                                                        onOneToManyDelete,
                                                                        onOneToManyAdd,
                                                                        onOneToManyAssign,
                                                                        onBeforeOpenSelectModal,
                                                                    }) => {
    const [showImageModal, setShowImageModal] = useState(false);
    const [enlargedChildImage, setEnlargedChildImage] = useState<FileData | null>(null);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [isManyToOneLoading, setIsManyToOneLoading] = useState(false);
    const selectedIdRef = useRef<any>(null);
    const value = getValueByPath(row, column.dataPath);
    const isBoolean = column.columnType === 'boolean';
    // A user-bound column (user, created-by, updated-by) is a relation like any other: the value is {id, …}.
    const isManyToOne = ['many-to-one', 'user', 'created-by', 'updated-by'].includes(column.columnType);
    const isOneToMany = column.columnType === 'one-to-many';
    const isTimestamp = column.columnType === 'timestamp';
    const isTime = column.columnType === 'time';
    const isInterval = column.columnType === 'interval';
    const isDate = column.columnType === 'date';
    const isMonthYear = column.columnType === 'month-year';
    const isFile = ['file', 'image'].includes(column.columnType);
    const isPassword = column.columnType === 'password';
    const isUrl = column.columnType === 'url';
    const isMarkdown = column.columnType === 'markdown';
    const columnUi = resolveAppearance(column.appearance, 'column');
    const rowColumnUi = resolveAppearance(row._appearance?.column?.[column.code], 'rule');
    const cellUi = mergeUi(columnUi, rowColumnUi);
    const tdClassName = cellUi.className || undefined;
    const tdStyle = cellUi.style;
    const {badgeClass, badgeStyle} = cellUi;
    // `variant: short` cuts the shown text to a fixed width; the title keeps the whole of it.
    const shortText = (text: string): React.ReactNode => cellUi.truncate && text
        ? <span className="d-inline-block text-truncate align-bottom" style={{maxWidth: '16rem'}} title={text}>{text}</span>
        : text;

    // Password columns never display their value
    if (isPassword) {
        return <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}></td>;
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onCellBlur();
        }
    };

    if (isManyToOne) {
        let displayValue = '';
        let hasProperDisplayValue = false;
        if (value && typeof value === 'object') {
            const targetColumnName = column.targetColumn?.columnName;
            if (targetColumnName && value[targetColumnName] !== undefined) {
                displayValue = value[targetColumnName];
                hasProperDisplayValue = true;
            } else {
                // Fallback: use first visible column from relatedScreen
                const visibleColumns = column.relatedScreen?.columns
                    ?.filter(col => !col.screenHidden)
                    .sort((a, b) => a.sortOrder - b.sortOrder) || [];
                const firstVisibleColumn = visibleColumns[0];
                if (firstVisibleColumn && value[firstVisibleColumn.code] !== undefined) {
                    displayValue = value[firstVisibleColumn.code];
                    hasProperDisplayValue = true;
                } else if (value.name) {
                    displayValue = value.name;
                    hasProperDisplayValue = true;
                } else {
                    displayValue = value.id || '';
                }
            }
        } else {
            displayValue = value || '';
        }

        // Clear loading when we have proper display value for the selected ID
        if (isManyToOneLoading && hasProperDisplayValue && value?.id === selectedIdRef.current) {
            setIsManyToOneLoading(false);
            selectedIdRef.current = null;
        }

        const relatedScreenId = column.relatedScreen?.id;

        if (column.editable && relatedScreenId) {
            const handleSelect = (selectedRow: any) => {
                if (onDirectUpdate) {
                    selectedIdRef.current = selectedRow.id;
                    setIsManyToOneLoading(true);
                    onDirectUpdate(row, column, selectedRow.id);
                }
            };

            const openSelectModal = () => {
                onBeforeOpenSelectModal?.(row, column);
                setShowSelectModal(true);
            };

            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => e.stopPropagation()}>
                    {isManyToOneLoading ? (
                        <div className="d-inline-flex align-items-center">
                            <Spinner animation="border" size="sm" className="text-muted" />
                        </div>
                    ) : displayValue ? (
                        <div className="v5-people">
                            <span className="v5-person" onClick={openSelectModal} style={{cursor: 'pointer'}}>
                                <span>{String(displayValue)}</span>
                                {!column.required && (
                                    <button
                                        type="button"
                                        className="v5-person-clear"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onDirectUpdate) {
                                                onDirectUpdate(row, column, null);
                                            }
                                        }}
                                        aria-label="Zrušiť priradenie"
                                    >
                                        <IconX size={12} />
                                    </button>
                                )}
                            </span>
                        </div>
                    ) : (
                        <div className="v5-people">
                            <button
                                type="button"
                                className="v5-assign"
                                onClick={openSelectModal}
                            >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 5v14M5 12h14"/>
                                </svg>
                                Priradiť
                            </button>
                        </div>
                    )}
                    <SelectModal
                        show={showSelectModal}
                        onHide={() => setShowSelectModal(false)}
                        screenId={relatedScreenId}
                        onSelect={handleSelect}
                        screenData={column.relatedScreen}
                    />
                </td>
            );
        }

        return (
            <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
                {shortText(displayValue !== null && displayValue !== undefined ? String(displayValue) : '')}
            </td>
        );
    }

    if (isOneToMany) {
        // A many-to-many link is described entirely by the related screen's
        // create form - see resolveAssignShape. Such a row IS the relation, so
        // the only sensible action is picking which records are linked; a plain
        // "+" would offer to author a link row by hand, which is the same thing
        // done worse. Ordinary one-to-many children keep the "+" instead.
        const assignShape = resolveAssignShape(column);
        const isManyToMany = !!assignShape;

        const canAddChild = !isManyToMany && column.editable && !!column.relatedScreen && (column.relatedScreen as any).createForms?.length > 0 && !!onOneToManyAdd;

        const renderAddButton = () => {
            if (!canAddChild) return null;
            return (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onOneToManyAdd!(column, row);
                    }}
                    className="btn btn-sm btn-label-primary"
                    style={{ padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
                    title="Pridať"
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14"/>
                    </svg>
                </button>
            );
        };

        // A link column has nothing to edit in place - the row IS the relation,
        // so picking what is linked is the only action there is. Gating it on
        // `editable` would leave such a column with no way to be filled at all.
        const canAssignChild = !!onOneToManyAssign && isManyToMany;

        const renderAssignButton = () => {
            if (!canAssignChild) return null;
            return (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onOneToManyAssign!(column, row);
                    }}
                    className="btn btn-sm btn-label-primary"
                    style={{ padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
                    title="Priradiť existujúci"
                >
                    <IconLink size={12} />
                </button>
            );
        };

        if (Array.isArray(value)) {
            if(value.length === 0) {
                return (
                    <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => (canAddChild || canAssignChild) ? e.stopPropagation() : undefined}>
                        <div className="d-flex gap-1 align-items-center">
                            {renderAddButton()}
                            {renderAssignButton()}
                        </div>
                    </td>
                );
            }

            const relatedColumns = column.relatedScreen?.columns?.filter(col => !col.screenHidden).sort((a, b) => a.sortOrder - b.sortOrder) || [];
            const canDeleteChild = !!column.relatedScreen?.deleteEnabled && !!onOneToManyDelete;

            const renderDeleteButton = (childId: any) => {
                if (!canDeleteChild || childId === undefined || childId === null) return null;
                return (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOneToManyDelete!(column, childId);
                        }}
                        className="p-0 ms-1 border-0 bg-transparent"
                        style={{ lineHeight: 1, fontSize: '0.75rem', color: 'inherit', cursor: 'pointer' }}
                        aria-label="Odstrániť"
                        title="Odstrániť"
                    >
                        <IconX size={12} />
                    </button>
                );
            };

            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => (canDeleteChild || canAddChild || canAssignChild) ? e.stopPropagation() : undefined}>
                    <div className="d-flex gap-1 flex-wrap align-items-start">
                        {value.map((item: any, index: number) => {
                            if (relatedColumns.length > 0) {
                                const parts = relatedColumns
                                    .map(col => {
                                        const val = item[col.code];
                                        if (val === null || val === undefined || val === '') return null;
                                        // A file is named by its file name - the generic walk below would
                                        // land on the first text it holds, which is its address.
                                        if (['file', 'image'].includes(col.columnType) && typeof val === 'object' && (val.filename || val.url)) {
                                            const file = val as FileData;
                                            return {
                                                label: col.displayName,
                                                value: getFileName(file),
                                                image: file.url && isDisplayableImage(file.mimeType) ? file : undefined,
                                            };
                                        }
                                        const resolveValue = (v: any, depth = 0): any => {
                                            if (depth > 10) return v?.id || '';
                                            if (v === null || v === undefined || v === '') return v;
                                            if (typeof v === 'boolean') return v ? 'ANO' : 'NIE';
                                            if (typeof v !== 'object') return v;
                                            if (v.name !== undefined) return v.name;
                                            for (const key of Object.keys(v)) {
                                                if (key === 'id' || key === 'created_at' || key === 'updated_at') continue;
                                                const nested = v[key];
                                                if (typeof nested === 'string') return nested;
                                                if (typeof nested === 'object' && nested !== null) {
                                                    const resolved = resolveValue(nested, depth + 1);
                                                    if (resolved !== null && resolved !== undefined && typeof resolved !== 'object') return resolved;
                                                }
                                            }
                                            return v.id || '';
                                        };
                                        const displayVal = resolveValue(val);
                                        return { label: col.displayName, value: displayVal };
                                    })
                                    .filter(Boolean) as { label: string; value: any; image?: FileData }[];

                                if (parts.length === 0) return null;

                                // A child row that shows nothing but one picture is drawn as that
                                // picture inside its badge. Next to other values it stays text.
                                const onlyImage = parts.length === 1 ? parts[0].image : undefined;
                                if (onlyImage) {
                                    return (
                                        <Badge key={index} bg="primary" className="text-white d-inline-flex align-items-center">
                                            <span
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEnlargedChildImage(onlyImage);
                                                }}
                                                style={{cursor: 'pointer'}}
                                            >
                                                <AuthenticatedImage
                                                    url={onlyImage.url!}
                                                    alt={parts[0].value}
                                                    // minWidth: a narrow column would otherwise squeeze the picture.
                                                    style={{width: '40px', minWidth: '40px', height: '40px', objectFit: 'cover'}}
                                                />
                                            </span>
                                            {renderDeleteButton(item.id)}
                                        </Badge>
                                    );
                                }

                                if (parts.length === 1) {
                                    return (
                                        <Badge key={index} bg="primary" className="text-white d-inline-flex align-items-center" style={{ userSelect: 'text' }}>
                                            {parts[0].value}
                                            {renderDeleteButton(item.id)}
                                        </Badge>
                                    );
                                }

                                return (
    <Badge key={index} bg="primary" className="d-flex flex-wrap align-items-center p-0 overflow-hidden" style={{ userSelect: 'text', borderRadius: '6px' }}>
                                        {parts.map((part, partIndex) => (
                                            <span
                                                key={partIndex}
                                                className="px-1 py-0"
                                                style={partIndex < parts.length - 1 ? { borderRight: '1px solid color-mix(in srgb, currentColor 30%, transparent)' } : undefined}
                                            >
                                                <small className="opacity-75">{part.label}:</small> {part.value}
                                            </span>
                                        ))}
                                        {canDeleteChild && item.id !== undefined && (
                                            <span className="px-1 py-0" style={{ borderLeft: '1px solid color-mix(in srgb, currentColor 30%, transparent)' }}>
                                                {renderDeleteButton(item.id)}
                                            </span>
                                        )}
                                    </Badge>
                                );
                            } else {
                                const displayValue = (column.targetColumn?.columnName && item[column.targetColumn.columnName]) || item.name || item.id || String(item);
                                if (!displayValue) return null;
                                return (
                                    <Badge key={index} bg="primary" className="text-white d-inline-flex align-items-center" style={{ userSelect: 'text' }}>
                                        {displayValue}
                                        {renderDeleteButton(item.id)}
                                    </Badge>
                                );
                            }
                        })}
                        {renderAddButton()}
                        {renderAssignButton()}
                    </div>
                    {enlargedChildImage && (
                        // The modal is a child of this cell in React's tree: a click inside it must not reach the row.
                        <span onClick={(e) => e.stopPropagation()}>
                            <ImageModal
                                show
                                onHide={() => setEnlargedChildImage(null)}
                                fileData={enlargedChildImage}
                                filename={getFileName(enlargedChildImage)}
                            />
                        </span>
                    )}
                </td>
            );
        }
        return (
            <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
                <Badge bg="primary" className="text-white" style={{ userSelect: 'text' }}>
                    {value !== null && value !== undefined ? String(value) : '0'}
                </Badge>
            </td>
        );
    }

    if (isBoolean) {
        if (column.editable) {
            const boolValue = value === true || value === 1 || value === '1' || value === 'true';
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => e.stopPropagation()}>
                    <div className="form-check form-switch mb-0">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={boolValue}
                            onChange={(e) => {
                                const newValue = e.target.checked ? 1 : 0;
                                if (onDirectUpdate) {
                                    onDirectUpdate(row, column, newValue);
                                }
                            }}
                        />
                    </div>
                </td>
            );
        } else {
            const boolValue = value === true || value === 1 || value === '1' || value === 'true';
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
                    <Badge bg={boolValue ? 'success' : 'danger'} className="text-white" style={{ userSelect: 'text' }}>
                        {boolValue ? 'ANO' : 'NIE'}
                    </Badge>
                </td>
            );
        }
    }

    if (isTimestamp) {
        if (column.editable) {
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => e.stopPropagation()}>
                    <DateTimePicker
                        mode="datetime"
                        value={value}
                        onChange={(val) => {
                            if (onDirectUpdate) {
                                // DateTimePicker returns datetime-local format
                                // (YYYY-MM-DDTHH:mm); the API wants full ISO 8601
                                // with timezone — convert before sending.
                                onDirectUpdate(row, column, val ? datetimeLocalToISO(val) : null);
                            }
                        }}
                        className="form-control form-control-sm"
                    />
                </td>
            );
        } else {
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
                    {formatTimestampForDisplay(value)}
                </td>
            );
        }
    }

    if (isTime) {
        if (column.editable) {
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => e.stopPropagation()}>
                    <DateTimePicker
                        mode="time"
                        value={toTimeInput(value)}
                        onChange={(val) => {
                            if (onDirectUpdate) {
                                // Clearing yields '' — send null so the column
                                // is set to NULL instead of an invalid ''.
                                onDirectUpdate(row, column, val || null);
                            }
                        }}
                        className="form-control form-control-sm"
                    />
                </td>
            );
        } else {
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
                    {toTimeInput(value)}
                </td>
            );
        }
    }

    if (isInterval) {
        if (column.editable) {
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => e.stopPropagation()}>
                    <DurationPicker
                        className="form-control form-control-sm"
                        style={{width: '7rem'}}
                        value={isEditing ? (editValue ?? '') : secondsToInterval(value)}
                        onChange={onEditValueChange}
                        onBlur={onCellBlur}
                        onKeyDown={handleKeyDown}
                        onFocus={(e) => onCellClick(row, column, e as any)}
                        isClearable={false}
                    />
                </td>
            );
        }
        return (
            <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
                {secondsToInterval(value)}
            </td>
        );
    }

    if (isMonthYear) {
        if (column.editable) {
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => e.stopPropagation()}>
                    <DateTimePicker
                        mode="month"
                        value={value}
                        onChange={(val) => {
                            if (onDirectUpdate) {
                                // Clearing the picker yields '' — send null (see date handling below)
                                onDirectUpdate(row, column, val || null);
                            }
                        }}
                        className="form-control form-control-sm"
                    />
                </td>
            );
        }
        return (
            <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
                {formatMonthYearForDisplay(value)}
            </td>
        );
    }

    if (isDate) {
        if (column.editable) {
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => e.stopPropagation()}>
                    <DateTimePicker
                        mode="date"
                        value={value}
                        onChange={(val) => {
                            if (onDirectUpdate) {
                                // Clearing the picker yields '' — send null so
                                // the date column is set to NULL, not '' (which
                                // Postgres rejects as invalid date syntax).
                                onDirectUpdate(row, column, val || null);
                            }
                        }}
                        className="form-control form-control-sm"
                    />
                </td>
            );
        } else {
            const match = typeof value === 'string' ? value.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
            const MONTHS_SK = ["jan","feb","mar","apr","máj","jún","júl","aug","sep","okt","nov","dec"];
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
                    {match ? (
                        <div className="v5-date">
                            <span className="v5-date-d">{parseInt(match[3], 10)}</span>
                            <span className="v5-date-m">{MONTHS_SK[parseInt(match[2], 10) - 1]}</span>
                        </div>
                    ) : formatDateForDisplay(value)}
                </td>
            );
        }
    }

    if (isUrl) {
        if (column.editable) {
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex align-items-center gap-1">
                        <input
                            type="url"
                            className="form-control form-control-sm"
                            style={{width: 'auto'}}
                            value={isEditing ? (editValue ?? '') : (value ?? '')}
                            onChange={(e) => onEditValueChange(e.target.value)}
                            onBlur={onCellBlur}
                            onKeyDown={handleKeyDown}
                            onFocus={(e) => onCellClick(row, column, e as any)}
                        />
                        {value && (
                            <a href={value} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Otvoriť odkaz">
                                <IconExternalLink size={16} className="text-primary" />
                            </a>
                        )}
                    </div>
                </td>
            );
        }

        return (
            <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
                {value ? (
                    <a href={value} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title={value}>
                        <IconExternalLink size={16} className="text-primary" />
                    </a>
                ) : null}
            </td>
        );
    }

    if (isMarkdown) {
        const renderMarkdownLinks = (text: string) => {
            const parts = text.split(/(\[[^\]]*\]\([^)]+\))/g);
            return parts.map((part, index) => {
                const match = part.match(/^\[([^\]]*)\]\(([^)]+)\)$/);
                if (match) {
                    return (
                        <a key={index} href={match[2]} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                            {match[1]}
                        </a>
                    );
                }
                return part;
            });
        };

        if (column.editable) {
            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => e.stopPropagation()}>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{width: 'auto'}}
                        value={isEditing ? (editValue ?? '') : (value ?? '')}
                        onChange={(e) => onEditValueChange(e.target.value)}
                        onBlur={onCellBlur}
                        onKeyDown={handleKeyDown}
                        onFocus={(e) => onCellClick(row, column, e as any)}
                    />
                </td>
            );
        }

        return (
            <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
                {value ? renderMarkdownLinks(String(value)) : ''}
            </td>
        );
    }

    if (isFile) {
        const fileData = value as FileData | null;
        if (fileData && (fileData.filename || fileData.url)) {
            const isImage = isDisplayableImage(fileData.mimeType);
            const filename = getFileName(fileData);

            return (
                <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} data-column-url={fileData.url || ''} onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex align-items-center gap-2">
                        {/* Image thumbnail - clickable to enlarge */}
                        {isImage && fileData.url && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowImageModal(true);
                                }}
                                style={{cursor: 'pointer'}}
                            >
                                <AuthenticatedImage
                                    url={fileData.url}
                                    alt={filename}
                                    style={{width: '40px', height: '40px', objectFit: 'cover'}}
                                />
                            </div>
                        )}
                        {isImage && fileData.data && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowImageModal(true);
                                }}
                                style={{cursor: 'pointer'}}
                            >
                                <img
                                    src={`data:${fileData.mimeType};base64,${fileData.data}`}
                                    alt={filename}
                                    style={{width: '40px', height: '40px', objectFit: 'cover'}}
                                />
                            </div>
                        )}

                        {/* Filename — only for non-images (images show a thumbnail) */}
                        {!isImage && (
                            <span className="text-truncate" style={{maxWidth: '150px'}}>
                                {filename}
                            </span>
                        )}

                        {/* Download button — for all files, including images */}
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={(e) => {
                                e.stopPropagation();
                                downloadFile(fileData);
                            }}
                            title="Stiahnuť súbor"
                        >
                            <IconDownload size={16}/>
                        </Button>
                    </div>
                    {/* Image modal for fullsize preview */}
                    {isImage && (
                        <ImageModal
                            show={showImageModal}
                            onHide={() => setShowImageModal(false)}
                            fileData={fileData}
                            filename={filename}
                        />
                    )}
                </td>
            );
        }
        return <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}></td>;
    }

    if (column.editable) {
        const isNumeric = isNumericFieldType(column.columnType);
        const isInteger = isIntegerFieldType(column.columnType);
        const isFreeText = ['string', 'text'].includes(column.columnType);

        return (
            <td className={tdClassName} style={tdStyle} data-column-type={column.columnType} onClick={(e) => e.stopPropagation()}>
                <input
                    type="text"
                    inputMode={isInteger ? "numeric" : isNumeric ? "decimal" : undefined}
                    className="form-control form-control-sm"
                    style={isFreeText ? undefined : {width: 'auto', ...(isNumeric ? {maxWidth: '100px'} : {})}}
                    value={isEditing ? (editValue ?? '') : (value ?? '')}
                    onChange={(e) => {
                        const val = e.target.value;
                        const accepts = isInteger ? isValidIntegerInput : isNumeric ? isValidNumericInput : null;
                        if (accepts === null || accepts(val)) {
                            onEditValueChange(val);
                        }
                    }}
                    onBlur={onCellBlur}
                    onKeyDown={handleKeyDown}
                    onFocus={(e) => onCellClick(row, column, e as any)}
                />
            </td>
        );
    }

    const displayValue = value !== null && value !== undefined ? String(value) : '';
    return (
        <td className={tdClassName} style={tdStyle} data-column-type={column.columnType}>
            {badgeClass && displayValue
                ? <span className={badgeClass} style={badgeStyle} title={cellUi.truncate ? displayValue : undefined}>{shortText(displayValue)}</span>
                : shortText(displayValue)}
        </td>
    );
};
